import { useState } from "react"
import { supabase } from "../lib/supabase"

interface Props {
  onLanguageChange: (l: "tr" | "en") => void
}

export const Login = ({ onLanguageChange }: Props) => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [mode, setMode] = useState<"login" | "signup" | "reset">("login")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [resetSent, setResetSent] = useState(false)
  const [language, setLanguage] = useState<"tr" | "en">("en")

  const handleLanguage = (l: "tr" | "en") => {
    setLanguage(l)
    onLanguageChange(l)
  }

  const t = language === "tr" ? {
    login: "Giriş Yap",
    signup: "Kayıt Ol",
    emailPlaceholder: "email@adresin.com",
    passwordPlaceholder: "Şifren",
    loginBtn: "Giriş Yap",
    signupBtn: "Kayıt Ol",
    loading: "Yükleniyor...",
    forgotPassword: "Şifremi unuttum",
    noAccount: "Hesabın yok mu? Kayıt ol",
    hasAccount: "Zaten hesabın var mı? Giriş yap",
    backToLogin: "← Giriş ekranına dön",
    resetTitle: "Şifre sıfırlama",
    resetBtn: "Sıfırlama maili gönder",
    resetSent: "Mail gönderildi. Gelen kutunu kontrol et.",
    errorInvalid: "Email veya şifre hatalı.",
    errorGeneral: "Bir hata oluştu. Tekrar dene.",
  } : {
    login: "Log In",
    signup: "Sign Up",
    emailPlaceholder: "your@email.com",
    passwordPlaceholder: "Your password",
    loginBtn: "Log In",
    signupBtn: "Sign Up",
    loading: "Loading...",
    forgotPassword: "Forgot password",
    noAccount: "No account? Sign up",
    hasAccount: "Already have an account? Log in",
    backToLogin: "← Back to login",
    resetTitle: "Reset password",
    resetBtn: "Send reset email",
    resetSent: "Email sent. Check your inbox.",
    errorInvalid: "Invalid email or password.",
    errorGeneral: "Something went wrong. Try again.",
  }

  const handleSubmit = async () => {
    setError("")
    if (!email.trim() || !password.trim()) return
    setLoading(true)

    if (mode === "login") {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) setError(t.errorInvalid)
    } else {
      const { data, error } = await supabase.auth.signUp({ email, password })
if (error) {
  if (error.message.includes("already registered") || error.message.includes("already exists")) {
    setError(language === "tr" ? "Bu email zaten kayıtlı. Giriş yap." : "This email is already registered. Log in.")
  } else {
    setError(t.errorGeneral)
  }
} else if (data.user && data.user.identities?.length === 0) {
  setError(language === "tr" ? "Bu email zaten kayıtlı. Giriş yap." : "This email is already registered. Log in.")
}
    }

    setLoading(false)
  }

  const handleReset = async () => {
    setError("")
    if (!email.trim()) return
    setLoading(true)
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${process.env.REACT_APP_SITE_URL || window.location.origin}/reset-password`,
    })
    setResetSent(true)
    setLoading(false)
  }

  return (
    <div className="app">
      <div className="header">
        <h1>DECISION ZERO</h1>
        <div className="lang">
          <button onClick={() => handleLanguage("en")} disabled={language === "en"}>EN</button>
          <button onClick={() => handleLanguage("tr")} disabled={language === "tr"}>TR</button>
        </div>
      </div>

      <div className="step-container" style={{ flex: "none" }}>
        <div className="step-label" style={{ textAlign: "center", marginBottom: "24px" }}>
          BLINDSPOT
        </div>

        {mode === "reset" ? (
          <div>
            <div className="question-text" style={{ marginBottom: "20px", textAlign: "center" }}>
              {t.resetTitle}
            </div>
            {resetSent ? (
              <div style={{ textAlign: "center", color: "var(--green)", fontSize: "15px" }}>
                {t.resetSent}
              </div>
            ) : (
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  style={inputStyle}
                />
                <button onClick={handleReset} disabled={loading} className="btn-primary" style={{ opacity: loading ? 0.4 : 1 }}>
                  {loading ? t.loading : t.resetBtn}
                </button>
              </div>
            )}
            <button onClick={() => setMode("login")} style={linkStyle}>
              {t.backToLogin}
            </button>
          </div>
        ) : (
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={(e) => { if (e.key === "Enter") handleSubmit() }}
              placeholder={t.emailPlaceholder}
              style={inputStyle}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => { if (e.key === "Enter") handleSubmit() }}
              placeholder={t.passwordPlaceholder}
              style={inputStyle}
            />

            {error && (
              <div style={{ color: "var(--red)", fontSize: "13px", marginBottom: "12px" }}>
                {error}
              </div>
            )}

            <button onClick={handleSubmit} disabled={loading} className="btn-primary" style={{ opacity: loading ? 0.4 : 1 }}>
              {loading ? t.loading : mode === "login" ? t.loginBtn : t.signupBtn}
            </button>

            {mode === "login" && (
              <button onClick={() => setMode("reset")} style={linkStyle}>
                {t.forgotPassword}
              </button>
            )}

            <button onClick={() => setMode(mode === "login" ? "signup" : "login")} style={linkStyle}>
              {mode === "login" ? t.noAccount : t.hasAccount}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(0,0,0,0.2)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: "12px",
  padding: "14px 16px",
  color: "#e5e7eb",
  fontFamily: "inherit",
  fontSize: "16px",
  outline: "none",
  marginBottom: "12px",
}

const linkStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "center",
  marginTop: "12px",
  background: "none",
  border: "none",
  color: "var(--text-muted)",
  fontSize: "13px",
  cursor: "pointer",
  padding: "4px",
}
